import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FiSearch, FiX, FiPhone, FiStar, FiTruck, FiPackage, FiUsers, FiAlertCircle, FiClock, FiUser, FiMail } from "react-icons/fi";
import {
  AvailableRiderItem,
  UnassignedOrder,
  UnassignedOrdersPagination,
  PendingLogisticRequest,
  PendingOrderItem,
  PendingRequestRider,
} from "../../types/riderAssignment";

interface FlatPendingRow {
  requestId: string;
  rider: PendingRequestRider | null;
  order: PendingOrderItem;
}

interface DispatcherDetail {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  verified: boolean;
  status: string;
  onlineStatus?: string;
  blocked: boolean;
  bikeModel_Color?: string;
  vehiclePlate?: string;
  createdAt?: string;
}

const getToken = (): string | undefined => {
  const auth = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  return jsonData?.token;
};

const authHeaders = (token?: string) =>
  token ? { Authorization: `Bearer ${token}` } : undefined;

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(amount);

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export default function RiderAssignment() {
  const [orders, setOrders] = useState<UnassignedOrder[]>([]);
  const [pagination, setPagination] = useState<UnassignedOrdersPagination | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersLoading, setOrdersLoading] = useState(true);

  const [riders, setRiders] = useState<AvailableRiderItem[]>([]);
  const [ridersLoading, setRidersLoading] = useState(true);

  const [orderSearch, setOrderSearch] = useState("");

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<UnassignedOrder | null>(null);
  const [selectedRiderId, setSelectedRiderId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [riderSearch, setRiderSearch] = useState("");

  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignOrder, setReassignOrder] = useState<FlatPendingRow | null>(null);
  const [reassignRiderId, setReassignRiderId] = useState<string | null>(null);
  const [reassigning, setReassigning] = useState(false);
  const [reassignRiderSearch, setReassignRiderSearch] = useState("");

  const [dispatchers, setDispatchers] = useState<DispatcherDetail[]>([]);
  const [dispatchersLoading, setDispatchersLoading] = useState(true);
  const [dispatcherSearch, setDispatcherSearch] = useState("");

  
  const [pendingRequests, setPendingRequests] = useState<PendingLogisticRequest[]>([]);
  const [pendingPagination, setPendingPagination] = useState<UnassignedOrdersPagination | null>(null);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingSearch, setPendingSearch] = useState("");

  const flatPendingRows: FlatPendingRow[] = useMemo(() => {
    return pendingRequests.flatMap((req) =>
      req.pendingOrders.map((order) => ({
        requestId: req.requestId,
        rider: req.rider,
        order,
      }))
    );
  }, [pendingRequests]);

  const [activeTab, setActiveTab] = useState<"unassigned" | "pending" | "dispatchers">("unassigned");

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/orders/unassigned-home-delivery?page=${currentPage}&limit=20`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setOrders(res.data.orders ?? []);
        setPagination(res.data.pagination ?? null);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch unassigned orders");
    } finally {
      setOrdersLoading(false);
    }
  }, [currentPage]);

  const fetchRiders = useCallback(async () => {
    setRidersLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/riders/available`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setRiders(res.data.riders ?? []);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch available riders");
    } finally {
      setRidersLoading(false);
    }
  }, []);

  const handleAssign = async () => {
    if (!selectedOrder || !selectedRiderId) return;
    setAssigning(true);
    try {
      const token = getToken();
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin-orders/${selectedOrder._id}/assign-rider`,
        { riderId: selectedRiderId },
        { headers: authHeaders(token) }
      );
      toast.success(res.data?.message || "Rider assigned successfully");
      setAssignModalOpen(false);
      setSelectedOrder(null);
      setSelectedRiderId(null);
      fetchOrders();
      fetchRiders();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to assign rider");
    } finally {
      setAssigning(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignOrder || !reassignRiderId) return;
    setReassigning(true);
    try {
      const token = getToken();
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/riders/orders/${reassignOrder.order.orderId}/reassign-rider`,
        { riderId: reassignRiderId },
        { headers: authHeaders(token) }
      );
      toast.success(res.data?.message || "Rider reassigned successfully");
      setReassignModalOpen(false);
      setReassignOrder(null);
      setReassignRiderId(null);
      fetchPendingRequests();
      fetchRiders();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to reassign rider");
    } finally {
      setReassigning(false);
    }
  };

  const openReassignModal = (row: FlatPendingRow) => {
    setReassignOrder(row);
    setReassignRiderId(null);
    setReassignRiderSearch("");
    setReassignModalOpen(true);
  };

  const filteredReassignRiders = riders.filter((r) => {
    const rId = r._id || r.id || r.riderId || r.phoneNumber;
    const currentRiderId = reassignOrder?.rider?._id || (reassignOrder?.rider as any)?.id || reassignOrder?.rider?.phoneNumber;
    if (rId && currentRiderId === rId) return false;
    if (!reassignRiderSearch.trim()) return true;
    const q = reassignRiderSearch.toLowerCase();
    const rName = r.fullName || (r.firstName && r.lastName ? `${r.firstName} ${r.lastName}` : "");
    return rName.toLowerCase().includes(q) || (r.phoneNumber || "").includes(q);
  });

  const fetchDispatchers = useCallback(async () => {
    setDispatchersLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllDispatcherDetails`,
        { headers: authHeaders(token) }
      );
      setDispatchers(Array.isArray(res.data) ? res.data : res.data?.data ?? []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch dispatcher details");
    } finally {
      setDispatchersLoading(false);
    }
  }, []);

  const filteredDispatchers = dispatchers.filter((d) => {
    if (!dispatcherSearch.trim()) return true;
    const q = dispatcherSearch.toLowerCase();
    const name = `${d.firstName || ""} ${d.lastName || ""}`.toLowerCase();
    return name.includes(q) || (d.email || "").toLowerCase().includes(q) || (d.phoneNumber || "").includes(q);
  });

  const fetchPendingRequests = useCallback(async () => {
    setPendingLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/riders/logistics/pending-requests?page=${pendingPage}&limit=20`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setPendingRequests(res.data.data ?? []);
        setPendingPagination(res.data.pagination ?? null);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch pending logistic requests");
    } finally {
      setPendingLoading(false);
    }
  }, [pendingPage]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchRiders(); }, [fetchRiders]);
  useEffect(() => { fetchPendingRequests(); }, [fetchPendingRequests]);
  useEffect(() => { fetchDispatchers(); }, [fetchDispatchers]);

  const filteredOrders = orders.filter((o) => {
    if (!orderSearch.trim()) return true;
    const q = orderSearch.toLowerCase();
    return (
      (o.orderNumber || "").toLowerCase().includes(q) ||
      (o.customerInfo?.name || "").toLowerCase().includes(q) ||
      (o.customerInfo?.phone || "").includes(q)
    );
  });

  const filteredRiders = riders.filter((r) => {
    if (!riderSearch.trim()) return true;
    const q = riderSearch.toLowerCase();
    const rName = r.fullName || (r.firstName && r.lastName ? `${r.firstName} ${r.lastName}` : "");
    return rName.toLowerCase().includes(q) || (r.phoneNumber || "").includes(q);
  });

  const filteredPending = flatPendingRows.filter((row) => {
    if (!pendingSearch.trim()) return true;
    const q = pendingSearch.toLowerCase();
    const riderName = row.rider
      ? `${row.rider.firstName || ""} ${row.rider.lastName || ""}`.toLowerCase()
      : "";
    const customerName = `${row.order?.customer?.firstName || ""} ${row.order?.customer?.lastName || ""}`.toLowerCase();
    return (
      (row.order?.orderNumber || "").toLowerCase().includes(q) ||
      customerName.includes(q) ||
      (row.order?.customer?.phoneNumber || "").includes(q) ||
      riderName.includes(q)
    );
  });

  const openAssignModal = (order: UnassignedOrder) => {
    setSelectedOrder(order);
    setSelectedRiderId(null);
    setRiderSearch("");
    setAssignModalOpen(true);
  };

  const SkeletonRows = ({ count, cols }: { count: number; cols: number }) => (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <tr key={`skel-${i}`} className="animate-pulse">
          {Array.from({ length: cols }).map((_, j) => (
            <td key={j} className="p-3.5">
              <div className="h-4 bg-gray-100 rounded w-full max-w-[120px]" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <div className="w-full min-h-screen p-4 xl:p-6 flex flex-col gap-6 font-sans">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-gray-950 tracking-tight">RIDERS</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            Assign available riders to unassigned home delivery orders
          </p>
        </div>
        <button
          onClick={() => { fetchOrders(); fetchRiders(); fetchPendingRequests(); fetchDispatchers(); }}
          className="px-4 py-2 bg-[#008060] text-white rounded-lg text-sm font-semibold hover:bg-[#006b50] transition-colors shadow-sm"
        >
          Refresh Data
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#EAF5EF] flex items-center justify-center">
              <FiUsers className="w-4 h-4 text-[#008060]" />
            </div>
            <p className="text-gray-400 text-xs font-semibold tracking-wide">Available Riders</p>
          </div>
          {ridersLoading ? (
            <div className="h-7 w-12 bg-gray-100 rounded animate-pulse mt-2" />
          ) : (
            <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">{riders.length}</h3>
          )}
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
              <FiPackage className="w-4 h-4 text-orange-500" />
            </div>
            <p className="text-gray-400 text-xs font-semibold tracking-wide">Unassigned Orders</p>
          </div>
          {ordersLoading ? (
            <div className="h-7 w-12 bg-gray-100 rounded animate-pulse mt-2" />
          ) : (
            <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">
              {pagination?.total ?? orders.length}
            </h3>
          )}
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <FiTruck className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-gray-400 text-xs font-semibold tracking-wide">Total Items</p>
          </div>
          {ordersLoading ? (
            <div className="h-7 w-12 bg-gray-100 rounded animate-pulse mt-2" />
          ) : (
            <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">
              {orders.reduce((sum, o) => sum + o.orderItems.length, 0)}
            </h3>
          )}
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
              <FiClock className="w-4 h-4 text-purple-500" />
            </div>
            <p className="text-gray-400 text-xs font-semibold tracking-wide">Pending Requests</p>
          </div>
          {pendingLoading ? (
            <div className="h-7 w-12 bg-gray-100 rounded animate-pulse mt-2" />
          ) : (
            <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">{pendingPagination?.total ?? pendingRequests.length}</h3>
          )}
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[110px]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
              <FiTruck className="w-4 h-4 text-teal-500" />
            </div>
            <p className="text-gray-400 text-xs font-semibold tracking-wide">Total Dispatchers</p>
          </div>
          {dispatchersLoading ? (
            <div className="h-7 w-12 bg-gray-100 rounded animate-pulse mt-2" />
          ) : (
            <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">{dispatchers.length}</h3>
          )}
        </div>
      </div>

      {/* Search bar + Tab navigation (inline) */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
        <div className="relative w-full md:max-w-md">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={
              activeTab === "unassigned"
                ? "Search by order number, customer name or phone…"
                : activeTab === "pending"
                ? "Search pending requests…"
                : "Search dispatchers by name, email or phone…"
            }
            value={activeTab === "unassigned" ? orderSearch : activeTab === "pending" ? pendingSearch : dispatcherSearch}
            onChange={(e) =>
              activeTab === "unassigned"
                ? setOrderSearch(e.target.value)
                : activeTab === "pending"
                ? setPendingSearch(e.target.value)
                : setDispatcherSearch(e.target.value)
            }
            className="w-full pl-11 pr-4 py-3 bg-white border border-gray-100 rounded-xl text-sm text-gray-800 focus:outline-none focus:border-[#008060] transition-all shadow-sm placeholder-gray-400"
          />
        </div>

        <div className="flex items-center gap-1 bg-white rounded-xl p-1 shadow-sm border border-gray-100 md:ml-auto shrink-0">
          <button
            onClick={() => setActiveTab("unassigned")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === "unassigned"
                ? "bg-[#008060] text-white shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Unassigned Orders
            {!ordersLoading && (
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === "unassigned" ? "bg-white/20" : "bg-gray-100"
              }`}>
                {pagination?.total ?? orders.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("pending")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === "pending"
                ? "bg-[#008060] text-white shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            Pending Requests
            {!pendingLoading && (
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === "pending" ? "bg-white/20" : "bg-gray-100"
              }`}>
                {pendingPagination?.total ?? pendingRequests.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("dispatchers")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === "dispatchers"
                ? "bg-[#008060] text-white shadow-sm"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            All Dispatchers
            {!dispatchersLoading && (
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === "dispatchers" ? "bg-white/20" : "bg-gray-100"
              }`}>
                {dispatchers.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab: Unassigned Orders ──────────────────────── */}
      {activeTab === "unassigned" && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-50 p-5">
          <h2 className="text-sm font-bold text-gray-700 mb-4">Unassigned Home Delivery Orders</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-[#EAF5EF] text-[#008060] text-xs font-bold rounded-lg">
                  <th className="p-3.5 pl-4 rounded-l-lg font-bold tracking-wide">Order #</th>
                  <th className="p-3.5 font-bold tracking-wide">Customer</th>
                  <th className="p-3.5 font-bold tracking-wide">Phone</th>
                  <th className="p-3.5 font-bold tracking-wide">Items</th>
                  <th className="p-3.5 font-bold tracking-wide">Total</th>
                  <th className="p-3.5 font-bold tracking-wide">Date</th>
                  <th className="p-3.5 pr-4 rounded-r-lg font-bold tracking-wide text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50/50 text-gray-700 font-medium">
                {ordersLoading ? (
                  <SkeletonRows count={5} cols={7} />
                ) : filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <tr key={order._id} className="hover:bg-gray-50/30 transition-colors">
                      <td className="p-3.5 pl-4 text-gray-900 font-semibold text-xs">{order.orderNumber}</td>
                      <td className="p-3.5 text-gray-700">{order.customerInfo?.name || "—"}</td>
                      <td className="p-3.5 text-gray-500 text-xs">{order.customerInfo?.phone || "—"}</td>
                      <td className="p-3.5 text-gray-900">{order.orderItems.length}</td>
                      <td className="p-3.5 text-[#008060] font-bold">{formatCurrency(order.allItemsTotalPrice)}</td>
                      <td className="p-3.5 text-gray-500 text-xs">{formatDate(order.orderDate)}</td>
                      <td className="p-3.5 pr-4 text-center">
                        <button
                          onClick={() => openAssignModal(order)}
                          disabled={riders.length === 0}
                          className="px-3 py-1.5 bg-[#008060] text-white rounded-lg text-xs font-bold hover:bg-[#006b50] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Assign Rider
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-400">
                      <FiAlertCircle className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                      No unassigned orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!ordersLoading && pagination && pagination.totalPages > 1 && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="text-xs font-semibold text-gray-600 disabled:text-gray-300 transition-colors"
              >
                Previous
              </button>
              <span className="text-xs text-gray-400">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={currentPage >= pagination.totalPages}
                className="text-xs font-semibold text-[#008060] disabled:text-gray-300 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Pending Logistic Requests ──────────────── */}
      {activeTab === "pending" && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-50 p-5">
          <h2 className="text-sm font-bold text-gray-700 mb-4">Pending Logistic Requests</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-purple-50 text-purple-600 text-xs font-bold rounded-lg">
                  <th className="p-3.5 pl-4 rounded-l-lg font-bold tracking-wide">Order #</th>
                  <th className="p-3.5 font-bold tracking-wide">Customer</th>
                  <th className="p-3.5 font-bold tracking-wide">Email</th>
                  <th className="p-3.5 font-bold tracking-wide">Rider</th>
                  <th className="p-3.5 font-bold tracking-wide">Order Status</th>
                  <th className="p-3.5 font-bold tracking-wide">Total</th>
                  <th className="p-3.5 font-bold tracking-wide">Status</th>
                  <th className="p-3.5 font-bold tracking-wide">Date</th>
                  <th className="p-3.5 pr-4 rounded-r-lg font-bold tracking-wide text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50/50 text-gray-700 font-medium">
                {pendingLoading ? (
                  <SkeletonRows count={5} cols={8} />
                ) : filteredPending.length > 0 ? (
                  filteredPending.map((row) => {
                    const riderName = row.rider
                      ? `${row.rider.firstName || ""} ${row.rider.lastName || ""}`.trim()
                      : null;
                    const customerName = `${row.order?.customer?.firstName || ""} ${row.order?.customer?.lastName || ""}`.trim();

                    return (
                      <tr key={`${row.requestId}-${row.order.orderId}`} className="hover:bg-gray-50/30 transition-colors">
                        <td className="p-3.5 pl-4 text-gray-900 font-semibold text-xs">
                          {row.order?.orderNumber || "—"}
                        </td>
                        <td className="p-3.5 text-gray-700">
                          {customerName || "—"}
                        </td>
                        <td className="p-3.5 text-gray-500 text-xs">
                          {row.order?.customer?.email || "—"}
                        </td>
                        <td className="p-3.5">
                          {riderName ? (
                            <span className="inline-flex items-center gap-1.5">
                              <FiUser className="w-3 h-3 text-gray-400" />
                              <span className="text-gray-700">{riderName}</span>
                              <span className={`ml-1 w-1.5 h-1.5 rounded-full inline-block ${
                                row.rider?.onlineStatus === "online" ? "bg-green-400" : "bg-gray-300"
                              }`} />
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-red-50 text-red-500">
                              Unassigned
                            </span>
                          )}
                        </td>
                        <td className="p-3.5">
                          <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                            row.order.orderStatus === "approved"
                              ? "bg-emerald-50 text-emerald-600"
                              : row.order.orderStatus === "delivered"
                              ? "bg-blue-50 text-blue-600"
                              : "bg-amber-50 text-amber-600"
                          }`}>
                            {row.order.orderStatus}
                          </span>
                        </td>
                        <td className="p-3.5 text-[#008060] font-bold">
                          {formatCurrency(row.order.totalAmount)}
                        </td>
                        <td className="p-3.5">
                          <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                            row.order.status === "pending"
                              ? "bg-amber-50 text-amber-600"
                              : row.order.status === "accepted"
                              ? "bg-emerald-50 text-emerald-600"
                              : row.order.status === "rejected"
                              ? "bg-red-50 text-red-500"
                              : "bg-gray-100 text-gray-600"
                          }`}>
                            {row.order.status}
                          </span>
                        </td>
                        <td className="p-3.5 text-gray-500 text-xs">
                          {formatDate(row.order.orderDate)}
                        </td>
                        <td className="p-3.5 pr-4 text-center">
                          <button
                            onClick={() => openReassignModal(row)}
                            disabled={riders.length === 0}
                            className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-bold hover:bg-purple-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Reassign Rider
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-400">
                      <FiClock className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                      No pending logistic requests.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!pendingLoading && pendingPagination && pendingPagination.totalPages > 1 && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => setPendingPage((p) => Math.max(1, p - 1))}
                disabled={pendingPage <= 1}
                className="text-xs font-semibold text-gray-600 disabled:text-gray-300 transition-colors"
              >
                Previous
              </button>
              <span className="text-xs text-gray-400">
                Page {pendingPagination.page} of {pendingPagination.totalPages}
              </span>
              <button
                onClick={() => setPendingPage((p) => Math.min(pendingPagination.totalPages, p + 1))}
                disabled={pendingPage >= pendingPagination.totalPages}
                className="text-xs font-semibold text-[#008060] disabled:text-gray-300 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: All Dispatchers ───────────────────────── */}
      {activeTab === "dispatchers" && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-50 p-5">
          <h2 className="text-sm font-bold text-gray-700 mb-4">All Dispatchers</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-teal-50 text-teal-600 text-xs font-bold rounded-lg">
                  <th className="p-3.5 pl-4 rounded-l-lg font-bold tracking-wide">Rider</th>
                  <th className="p-3.5 font-bold tracking-wide">Email</th>
                  <th className="p-3.5 font-bold tracking-wide">Phone</th>
                  <th className="p-3.5 font-bold tracking-wide">Status</th>
                  <th className="p-3.5 font-bold tracking-wide">Online</th>
                  <th className="p-3.5 font-bold tracking-wide">Bike Info</th>
                  <th className="p-3.5 pr-4 rounded-r-lg font-bold tracking-wide">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50/50 text-gray-700 font-medium">
                {dispatchersLoading ? (
                  <SkeletonRows count={5} cols={7} />
                ) : filteredDispatchers.length > 0 ? (
                  filteredDispatchers.map((d) => {
                    const fullName = `${d.firstName} ${d.lastName}`.trim();
                    const isOnline = d.onlineStatus === "online";

                    return (
                      <tr key={d._id} className="hover:bg-gray-50/30 transition-colors">
                        <td className="p-3.5 pl-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                              isOnline ? "bg-teal-100 text-teal-700" : "bg-gray-100 text-gray-500"
                            }`}>
                              {`${d.firstName?.[0] ?? ""}${d.lastName?.trim()?.[0] ?? ""}`.toUpperCase() || "?"}
                            </div>
                            <span className="font-semibold text-gray-900 text-sm">{fullName}</span>
                          </div>
                        </td>
                        <td className="p-3.5 text-gray-500 text-xs">{d.email}</td>
                        <td className="p-3.5 text-gray-500 text-xs">{d.phoneNumber}</td>
                        <td className="p-3.5">
                          <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                            d.status === "idle"
                              ? "bg-emerald-50 text-emerald-600"
                              : d.status === "unavailable"
                              ? "bg-red-50 text-red-500"
                              : d.status === "active" || d.status === "approved"
                              ? "bg-blue-50 text-blue-600"
                              : "bg-amber-50 text-amber-600"
                          }`}>
                            {d.status}
                          </span>
                        </td>
                        <td className="p-3.5">
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <span className={`w-2 h-2 rounded-full ${isOnline ? "bg-green-400" : "bg-gray-300"}`} />
                            <span className={isOnline ? "text-green-600 font-semibold" : "text-gray-400"}>
                              {isOnline ? "Online" : "Offline"}
                            </span>
                          </span>
                        </td>
                        <td className="p-3.5 text-xs text-gray-500">
                          {d.bikeModel_Color || d.vehiclePlate ? (
                            <div className="flex flex-col gap-0.5">
                              {d.bikeModel_Color && <span>{d.bikeModel_Color}</span>}
                              {d.vehiclePlate && (
                                <span className="text-[10px] font-bold text-gray-400">{d.vehiclePlate}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="p-3.5 text-gray-500 text-xs">
                          {d.createdAt ? formatDate(d.createdAt) : "—"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-400">
                      <FiUsers className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                      No dispatchers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Assign Rider Modal ──── */}
      {assignModalOpen && selectedOrder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { if (!assigning) setAssignModalOpen(false); }}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-base font-bold text-gray-900">Assign Rider</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Order {selectedOrder.orderNumber} — {selectedOrder.customerInfo?.name || "Unknown"}
                </p>
              </div>
              <button
                onClick={() => { if (!assigning) setAssignModalOpen(false); }}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <FiX className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Search riders */}
            <div className="px-5 pt-4">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search rider by name or phone…"
                  value={riderSearch}
                  onChange={(e) => setRiderSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:border-[#008060] placeholder-gray-400"
                />
              </div>
            </div>

            {/* Rider list */}
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {ridersLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="animate-pulse h-16 bg-gray-50 rounded-xl" />
                ))
              ) : filteredRiders.length > 0 ? (
                filteredRiders.map((rider) => {
                  const rId = rider._id || rider.id || rider.riderId || rider.phoneNumber || "";
                  const isSelected = selectedRiderId === rId;
                  return (
                    <button
                      key={rId || Math.random().toString()}
                      onClick={() => setSelectedRiderId(rId)}
                      className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${
                        isSelected
                          ? "border-[#008060] bg-[#EAF5EF]"
                          : "border-gray-100 bg-white hover:border-gray-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                            isSelected ? "bg-[#008060] text-white" : "bg-gray-100 text-gray-600"
                          }`}>
                            {(rider.fullName || (rider.firstName && rider.lastName ? `${rider.firstName} ${rider.lastName}` : "U")).split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{rider.fullName || (rider.firstName && rider.lastName ? `${rider.firstName} ${rider.lastName}` : "Unknown Rider")}</p>
                            <div className="flex items-center gap-3 mt-0.5">
                              <span className="flex items-center gap-1 text-xs text-gray-400">
                                <FiPhone className="w-3 h-3" /> {rider.phoneNumber}
                              </span>
                              {rider.averageRating != null && (
                                <span className="flex items-center gap-0.5 text-xs text-amber-500">
                                  <FiStar className="w-3 h-3" /> {Number(rider.averageRating).toFixed(1)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                          rider.riderType === "Internal"
                            ? "bg-blue-50 text-blue-600"
                            : "bg-orange-50 text-orange-600"
                        }`}>
                          {rider.riderType}
                        </span>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No available riders found.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-gray-100 flex items-center gap-3">
              <button
                onClick={() => setAssignModalOpen(false)}
                disabled={assigning}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedRiderId || assigning}
                className="flex-1 px-4 py-2.5 bg-[#008060] text-white rounded-xl text-sm font-bold hover:bg-[#006b50] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {assigning ? "Assigning…" : "Confirm Assignment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reassign Rider Modal ────────────────────────── */}
      {reassignModalOpen && reassignOrder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => { if (!reassigning) setReassignModalOpen(false); }}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-base font-bold text-gray-900">Reassign Rider</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Order {reassignOrder.order?.orderNumber}
                  {reassignOrder.rider
                    ? ` — Current: ${reassignOrder.rider.firstName || ""} ${reassignOrder.rider.lastName || ""}`
                    : " — No rider assigned"}
                </p>
              </div>
              <button
                onClick={() => { if (!reassigning) setReassignModalOpen(false); }}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <FiX className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Search riders */}
            <div className="px-5 pt-4">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search rider by name or phone…"
                  value={reassignRiderSearch}
                  onChange={(e) => setReassignRiderSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:border-purple-500 placeholder-gray-400"
                />
              </div>
            </div>

            {/* Rider list */}
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {ridersLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="animate-pulse h-16 bg-gray-50 rounded-xl" />
                ))
              ) : filteredReassignRiders.length > 0 ? (
                filteredReassignRiders.map((rider) => {
                  const rId = rider._id || rider.id || rider.riderId || rider.phoneNumber || "";
                  const isSelected = reassignRiderId === rId;
                  const currentRiderId = reassignOrder.rider?._id || (reassignOrder.rider as any)?.id || reassignOrder.rider?.phoneNumber;
                  const isCurrent = Boolean(currentRiderId && currentRiderId === rId);
                  return (
                    <button
                      key={rId || Math.random().toString()}
                      onClick={() => setReassignRiderId(rId)}
                      disabled={isCurrent}
                      className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${
                        isCurrent
                          ? "border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed"
                          : isSelected
                          ? "border-purple-500 bg-purple-50"
                          : "border-gray-100 bg-white hover:border-gray-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                            isCurrent
                              ? "bg-gray-200 text-gray-500"
                              : isSelected
                              ? "bg-purple-600 text-white"
                              : "bg-gray-100 text-gray-600"
                          }`}>
                            {(rider.fullName || (rider.firstName && rider.lastName ? `${rider.firstName} ${rider.lastName}` : "U")).split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">
                              {rider.fullName || (rider.firstName && rider.lastName ? `${rider.firstName} ${rider.lastName}` : "Unknown Rider")}
                              {isCurrent && (
                                <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">
                                  Current
                                </span>
                              )}
                            </p>
                            <div className="flex items-center gap-3 mt-0.5">
                              <span className="flex items-center gap-1 text-xs text-gray-400">
                                <FiPhone className="w-3 h-3" /> {rider.phoneNumber}
                              </span>
                              {rider.averageRating != null && (
                                <span className="flex items-center gap-0.5 text-xs text-amber-500">
                                  <FiStar className="w-3 h-3" /> {Number(rider.averageRating).toFixed(1)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                          rider.riderType === "Internal"
                            ? "bg-blue-50 text-blue-600"
                            : "bg-orange-50 text-orange-600"
                        }`}>
                          {rider.riderType}
                        </span>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No available riders found.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-gray-100 flex items-center gap-3">
              <button
                onClick={() => setReassignModalOpen(false)}
                disabled={reassigning}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReassign}
                disabled={!reassignRiderId || reassigning}
                className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-bold hover:bg-purple-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {reassigning ? "Reassigning…" : "Confirm Reassignment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
